import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Loader2, QrCode, Clock, Store, CheckCircle2, XCircle, 
  RefreshCw, AlertTriangle, DollarSign, Calendar, Shield,
  Smartphone
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useLanguage } from "../context/LanguageContext";

import { API_URL } from '../utils/apiConfig';

/**
 * BeneficiaryRedemption - Secure beneficiary access page
 * 
 * URL format: /b/:access_token
 * 
 * Features:
 * - Auto-refreshing QR code (10 min TTL)
 * - Purchase confirmation flow
 * - Bilingual (EN/ES) support
 * - Mobile-first responsive design
 */
export default function BeneficiaryRedemption() {
  const { accessToken } = useParams();
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authData, setAuthData] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading, active, expired, completed, error
  
  // QR countdown
  const [qrTimeRemaining, setQrTimeRemaining] = useState(600); // 10 minutes in seconds
  const [isRefreshingQr, setIsRefreshingQr] = useState(false);
  
  // Purchase confirmation
  const [pendingPurchase, setPendingPurchase] = useState(null);
  const [isProcessingConfirm, setIsProcessingConfirm] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  
  // Polling refs
  const pollingIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Translations
  const translations = {
    en: {
      loading: "Loading your authorization...",
      availableToSpend: "Available to spend",
      validAt: "Valid at",
      authValidUntil: "Purchase authorization valid until",
      qrRefreshesIn: "QR refreshes in",
      backupCode: "Backup Code",
      backupCodeHint: "If QR won't scan, the merchant can enter this code manually",
      refreshQr: "Generate New QR",
      refreshingQr: "Refreshing secure QR...",
      authExpired: "This purchase authorization has expired. The unused balance can no longer be redeemed.",
      authCompleted: "This purchase authorization has been fully used.",
      qrExpiredRefreshing: "Your QR code expired, but your balance is still available. A new QR code is being generated.",
      confirmPurchase: "Confirm Purchase",
      cancelPurchase: "Cancel",
      purchaseAt: "Purchase at",
      purchaseAmount: "Purchase amount",
      availableBefore: "Available before purchase",
      remainingAfter: "Remaining after purchase",
      waitingForScan: "Waiting for merchant to scan your QR...",
      purchaseSuccess: "Purchase completed successfully!",
      purchaseCancelled: "Purchase cancelled. Your balance is unchanged.",
      errorTitle: "Something went wrong",
      tryAgain: "Try Again",
      switchToSpanish: "Español",
      switchToEnglish: "English",
      showPhoneToMerchant: "Show this QR code to the merchant"
    },
    es: {
      loading: "Cargando tu autorización...",
      availableToSpend: "Saldo disponible",
      validAt: "Válido en",
      authValidUntil: "Autorización de compra válida hasta",
      qrRefreshesIn: "El código QR se actualiza en",
      backupCode: "Código de respaldo",
      backupCodeHint: "Si el QR no escanea, el comercio puede ingresar este código manualmente",
      refreshQr: "Generar nuevo código QR",
      refreshingQr: "Actualizando código QR seguro...",
      authExpired: "Esta autorización de compra ha vencido. El saldo restante ya no puede ser utilizado.",
      authCompleted: "Esta autorización de compra ha sido utilizada por completo.",
      qrExpiredRefreshing: "Tu código QR venció, pero tu saldo sigue disponible. Estamos generando un nuevo código QR.",
      confirmPurchase: "Confirmar compra",
      cancelPurchase: "Cancelar",
      purchaseAt: "Compra en",
      purchaseAmount: "Monto de la compra",
      availableBefore: "Saldo disponible",
      remainingAfter: "Saldo restante después de la compra",
      waitingForScan: "Esperando que el comercio escanee tu QR...",
      purchaseSuccess: "¡Compra completada exitosamente!",
      purchaseCancelled: "Compra cancelada. Tu saldo no ha cambiado.",
      errorTitle: "Algo salió mal",
      tryAgain: "Intentar de nuevo",
      switchToSpanish: "Español",
      switchToEnglish: "English",
      showPhoneToMerchant: "Muestra este código QR al comercio"
    }
  };
  
  const getText = (key) => translations[language]?.[key] || translations.en[key];

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format currency
  const formatDOP = (amount) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('DOP', 'RD$');
  };

  // Format date
  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString(language === 'es' ? 'es-DO' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Fetch authorization data
  const fetchAuthorizationData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/beneficiary/access/${accessToken}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to load authorization");
      }
      
      setAuthData(data.authorization);
      setQrData(data.qr);
      setStatus(data.status);
      
      // Calculate QR time remaining
      if (data.qr?.expires_at) {
        const expiresAt = new Date(data.qr.expires_at);
        const now = new Date();
        const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
        setQrTimeRemaining(remaining);
      }
      
      // Check for pending purchase request
      if (data.pending_purchase_request) {
        setPendingPurchase(data.pending_purchase_request);
      }
      
      setError(null);
    } catch (err) {
      console.error("Error fetching authorization:", err);
      setError(err.message);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  // Check for pending purchase requests
  const checkPendingPurchase = useCallback(async () => {
    if (!accessToken || status !== "active") return;
    
    try {
      const response = await fetch(`${API_URL}/api/beneficiary/access/${accessToken}/purchase-request`);
      const data = await response.json();
      
      if (data.has_pending_request) {
        setPendingPurchase(data.request);
      } else if (data.last_request_expired) {
        setPendingPurchase(null);
      }
    } catch (err) {
      console.error("Error checking purchase request:", err);
    }
  }, [accessToken, status]);

  // Regenerate QR
  const regenerateQr = useCallback(async () => {
    setIsRefreshingQr(true);
    
    try {
      const response = await fetch(`${API_URL}/api/beneficiary/access/${accessToken}/regenerate-qr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ security_reset: false })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to regenerate QR");
      }
      
      setQrData(data.qr);
      
      // Reset countdown
      if (data.qr?.expires_at) {
        const expiresAt = new Date(data.qr.expires_at);
        const now = new Date();
        const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
        setQrTimeRemaining(remaining);
      }
    } catch (err) {
      console.error("Error regenerating QR:", err);
      setError(err.message);
    } finally {
      setIsRefreshingQr(false);
    }
  }, [accessToken]);

  // Confirm purchase
  const confirmPurchase = async () => {
    if (!pendingPurchase) return;
    setIsProcessingConfirm(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/beneficiary/access/${accessToken}/purchase-request/${pendingPurchase.id}/confirm`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to confirm purchase");
      }
      
      setConfirmationResult({
        success: true,
        message: getText('purchaseSuccess'),
        redemption: data.redemption
      });
      
      // Refresh authorization data
      await fetchAuthorizationData();
      setPendingPurchase(null);
    } catch (err) {
      console.error("Error confirming purchase:", err);
      setConfirmationResult({
        success: false,
        message: err.message
      });
    } finally {
      setIsProcessingConfirm(false);
    }
  };

  // Cancel purchase
  const cancelPurchase = async () => {
    if (!pendingPurchase) return;
    setIsProcessingConfirm(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/beneficiary/access/${accessToken}/purchase-request/${pendingPurchase.id}/cancel`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to cancel purchase");
      }
      
      setConfirmationResult({
        success: true,
        cancelled: true,
        message: getText('purchaseCancelled')
      });
      
      setPendingPurchase(null);
    } catch (err) {
      console.error("Error cancelling purchase:", err);
    } finally {
      setIsProcessingConfirm(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (accessToken) {
      fetchAuthorizationData();
    }
  }, [accessToken, fetchAuthorizationData]);

  // QR countdown timer
  useEffect(() => {
    if (status !== "active" || !qrData) return;
    
    countdownIntervalRef.current = setInterval(() => {
      setQrTimeRemaining(prev => {
        if (prev <= 1) {
          // Auto-refresh QR when timer reaches 0
          regenerateQr();
          return 600; // Reset to 10 minutes
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [status, qrData, regenerateQr]);

  // Poll for purchase requests every 3 seconds
  useEffect(() => {
    if (status !== "active") return;
    
    pollingIntervalRef.current = setInterval(checkPendingPurchase, 3000);
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [status, checkPendingPurchase]);

  // Clear confirmation result after 5 seconds
  useEffect(() => {
    if (confirmationResult) {
      const timer = setTimeout(() => setConfirmationResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [confirmationResult]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 flex items-center justify-center p-4" data-testid="beneficiary-loading">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center" data-testid="beneficiary-loading-card">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" data-testid="loading-spinner" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900">{getText('loading')}</h1>
        </div>
      </div>
    );
  }

  // Error state
  if (status === "error" || error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-red-800 to-red-900 flex items-center justify-center p-4" data-testid="beneficiary-error">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center" data-testid="beneficiary-error-card">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">{getText('errorTitle')}</h1>
          <p className="text-slate-600 mb-6" data-testid="error-message">{error}</p>
          <Button 
            onClick={() => window.location.reload()} 
            className="bg-red-600 hover:bg-red-700"
            data-testid="error-retry-btn"
          >
            {getText('tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  // Expired state
  if (status === "expired") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 flex items-center justify-center p-4" data-testid="beneficiary-expired">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center" data-testid="beneficiary-expired-card">
          <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-amber-600" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-4">{getText('authValidUntil')}</h1>
          <p className="text-slate-600" data-testid="expired-message">{getText('authExpired')}</p>
          
          {authData && (
            <div className="mt-6 p-4 bg-slate-50 rounded-xl" data-testid="expired-merchant-info">
              <p className="text-sm text-slate-500">{getText('validAt')}</p>
              <p className="font-medium text-slate-800" data-testid="expired-merchant-name">{authData.merchant_name}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Completed state
  if (status === "completed") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 flex items-center justify-center p-4" data-testid="beneficiary-completed">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center" data-testid="beneficiary-completed-card">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-4" data-testid="completed-title">{getText('authCompleted')}</h1>
          
          {authData && (
            <div className="mt-6 p-4 bg-emerald-50 rounded-xl" data-testid="completed-merchant-info">
              <p className="text-sm text-emerald-600">{getText('validAt')}</p>
              <p className="font-medium text-emerald-800" data-testid="completed-merchant-name">{authData.merchant_name}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Active state - main UI
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900" data-testid="beneficiary-active">
      {/* Language toggle */}
      <div className="absolute top-4 right-4">
        <button
          onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm font-medium transition-colors"
          data-testid="beneficiary-language-toggle"
        >
          {language === 'es' ? getText('switchToEnglish') : getText('switchToSpanish')}
        </button>
      </div>

      <div className="max-w-md mx-auto px-4 py-8 pb-24">
        {/* Logo */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-white tracking-tight" data-testid="beneficiary-logo">PeCashGlobal</h1>
        </div>

        {/* Main card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" data-testid="beneficiary-main-card">
          {/* Balance header */}
          <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 p-6 text-center" data-testid="beneficiary-balance-header">
            <p className="text-emerald-100 text-sm mb-1">{getText('availableToSpend')}</p>
            <p className="text-4xl font-bold text-white" data-testid="beneficiary-available-balance">
              {formatDOP(authData?.remaining_amount_dop || 0)}
            </p>
          </div>

          {/* Merchant info */}
          <div className="p-4 border-b border-slate-100" data-testid="beneficiary-merchant-info">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                <Store className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">{getText('validAt')}</p>
                <p className="font-semibold text-slate-900" data-testid="beneficiary-merchant-name">{authData?.merchant_name}</p>
              </div>
            </div>
          </div>

          {/* QR Code section */}
          {!pendingPurchase && (
            <div className="p-6" data-testid="beneficiary-qr-section">
              {isRefreshingQr ? (
                <div className="text-center py-8" data-testid="qr-refreshing">
                  <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" />
                  <p className="text-slate-600">{getText('refreshingQr')}</p>
                </div>
              ) : qrData?.code ? (
                <>
                  <div className="flex justify-center mb-4">
                    <div className="p-3 bg-white rounded-2xl shadow-lg border-2 border-emerald-100" data-testid="qr-code-container">
                      <img 
                        src={qrData.code} 
                        alt="QR Code" 
                        className="w-48 h-48 sm:w-56 sm:h-56"
                        data-testid="beneficiary-qr-code"
                      />
                    </div>
                  </div>
                  
                  {/* Show phone hint */}
                  <div className="flex items-center justify-center gap-2 text-slate-500 text-sm mb-4" data-testid="qr-hint">
                    <Smartphone className="w-4 h-4" />
                    <span>{getText('showPhoneToMerchant')}</span>
                  </div>

                  {/* QR countdown */}
                  <div className="flex items-center justify-center gap-2 text-slate-600 mb-4" data-testid="qr-countdown">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">{getText('qrRefreshesIn')}</span>
                    <span className="font-mono font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded" data-testid="qr-time-remaining">
                      {formatTime(qrTimeRemaining)}
                    </span>
                  </div>

                  {/* Manual refresh button */}
                  <Button
                    onClick={regenerateQr}
                    variant="outline"
                    className="w-full"
                    disabled={isRefreshingQr}
                    data-testid="refresh-qr-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshingQr ? 'animate-spin' : ''}`} />
                    {getText('refreshQr')}
                  </Button>
                </>
              ) : (
                <div className="text-center py-8" data-testid="qr-generate-section">
                  <Button onClick={regenerateQr} className="bg-emerald-600 hover:bg-emerald-700" data-testid="generate-qr-btn">
                    <QrCode className="w-4 h-4 mr-2" />
                    {getText('refreshQr')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Pending Purchase Confirmation */}
          {pendingPurchase && !confirmationResult && (
            <div className="p-6 bg-amber-50 border-t-4 border-amber-400" data-testid="purchase-confirmation-section">
              <div className="text-center mb-4">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                  <DollarSign className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900" data-testid="confirm-purchase-title">{getText('confirmPurchase')}</h3>
              </div>

              <div className="space-y-3 mb-6" data-testid="purchase-details">
                <div className="flex justify-between items-center p-3 bg-white rounded-lg" data-testid="purchase-merchant-row">
                  <span className="text-slate-600 text-sm">{getText('purchaseAt')}</span>
                  <span className="font-semibold text-slate-900" data-testid="purchase-merchant-name">{pendingPurchase.merchant_name}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white rounded-lg" data-testid="purchase-amount-row">
                  <span className="text-slate-600 text-sm">{getText('purchaseAmount')}</span>
                  <span className="font-bold text-xl text-emerald-700" data-testid="purchase-amount">
                    {formatDOP(pendingPurchase.purchase_amount_dop)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white rounded-lg" data-testid="available-before-row">
                  <span className="text-slate-600 text-sm">{getText('availableBefore')}</span>
                  <span className="font-medium text-slate-800" data-testid="available-before-amount">
                    {formatDOP(pendingPurchase.available_balance_dop)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-emerald-100 rounded-lg" data-testid="remaining-after-row">
                  <span className="text-emerald-700 text-sm font-medium">{getText('remainingAfter')}</span>
                  <span className="font-bold text-emerald-800" data-testid="remaining-after-amount">
                    {formatDOP(pendingPurchase.remaining_after_dop)}
                  </span>
                </div>
              </div>

              <div className="flex gap-3" data-testid="purchase-actions">
                <Button
                  onClick={cancelPurchase}
                  variant="outline"
                  className="flex-1"
                  disabled={isProcessingConfirm}
                  data-testid="cancel-purchase-btn"
                >
                  {getText('cancelPurchase')}
                </Button>
                <Button
                  onClick={confirmPurchase}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={isProcessingConfirm}
                  data-testid="confirm-purchase-btn"
                >
                  {isProcessingConfirm ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    getText('confirmPurchase')
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Confirmation Result */}
          {confirmationResult && (
            <div className={`p-6 ${confirmationResult.success ? 'bg-emerald-50' : 'bg-red-50'} border-t-4 ${confirmationResult.success ? 'border-emerald-400' : 'border-red-400'}`} data-testid="confirmation-result">
              <div className="text-center">
                <div className={`w-16 h-16 rounded-full ${confirmationResult.success ? 'bg-emerald-100' : 'bg-red-100'} flex items-center justify-center mx-auto mb-3`}>
                  {confirmationResult.success ? (
                    <CheckCircle2 className={`w-8 h-8 ${confirmationResult.cancelled ? 'text-slate-600' : 'text-emerald-600'}`} data-testid="result-success-icon" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-600" data-testid="result-error-icon" />
                  )}
                </div>
                <p className={`font-semibold ${confirmationResult.success ? 'text-emerald-800' : 'text-red-800'}`} data-testid="confirmation-message">
                  {confirmationResult.message}
                </p>
                {confirmationResult.redemption && (
                  <p className="text-sm text-emerald-600 mt-2">
                    Receipt: {confirmationResult.redemption.receipt_id}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Backup code */}
          {authData?.backup_code && !pendingPurchase && (
            <div className="p-4 bg-slate-50 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-500 font-medium">{getText('backupCode')}</span>
                <Shield className="w-4 h-4 text-slate-400" />
              </div>
              <div className="font-mono text-2xl font-bold text-slate-800 tracking-widest text-center bg-white p-3 rounded-lg border border-slate-200">
                {authData.backup_code.match(/.{1,4}/g)?.join(' ')}
              </div>
              <p className="text-xs text-slate-500 mt-2 text-center">{getText('backupCodeHint')}</p>
            </div>
          )}

          {/* Authorization expiration */}
          <div className="p-4 bg-slate-100 flex items-center justify-center gap-2 text-slate-600 text-sm">
            <Calendar className="w-4 h-4" />
            <span>{getText('authValidUntil')}: {formatDate(authData?.authorization_expires_at)}</span>
          </div>
        </div>

        {/* Waiting indicator when no pending purchase */}
        {!pendingPurchase && !confirmationResult && status === "active" && (
          <div className="mt-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/80 text-sm">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              {getText('waitingForScan')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
